import { useCallback, useRef, useState } from "react";

interface DragState {
  draggingId: string | null;
  overIndex: number | null;
}

export function useDragReorder<T>({
  items,
  onReorder,
  getId,
  enabled,
}: {
  items: T[];
  onReorder: (reordered: T[]) => void;
  getId: (item: T) => string;
  enabled: boolean;
}) {
  const [dragState, setDragState] = useState<DragState>({ draggingId: null, overIndex: null });
  const listRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef(0);
  const dragItemIndex = useRef(-1);
  const didDrag = useRef(false);
  // Snapshot rects at drag start (before transforms distort them)
  const snapshotRects = useRef<DOMRect[]>([]);

  const handlePointerDown = useCallback(
    (id: string, e: React.PointerEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();

      const idx = items.findIndex((item) => getId(item) === id);
      if (idx < 0 || !listRef.current) return;

      // Snapshot all item rects before any transforms
      const children = Array.from(listRef.current.querySelectorAll<HTMLElement>("[data-step-id]"));
      snapshotRects.current = children.map((el) => el.getBoundingClientRect());

      dragItemIndex.current = idx;
      dragStartY.current = e.clientY;
      didDrag.current = false;
      setDragState({ draggingId: id, overIndex: idx });

      const onMove = (ev: PointerEvent) => {
        if (!listRef.current) return;
        const children = Array.from(
          listRef.current.querySelectorAll<HTMLElement>("[data-step-id]"),
        );
        const rects = snapshotRects.current;

        // Find the drop index using snapshotted midpoints
        let newIndex = children.length - 1;
        for (let i = 0; i < rects.length; i++) {
          const mid = rects[i]!.top + rects[i]!.height / 2;
          if (ev.clientY < mid) {
            newIndex = i;
            break;
          }
        }

        didDrag.current = true;
        setDragState((prev) => ({ ...prev, overIndex: newIndex }));

        // Apply transforms for visual feedback
        const draggedEl = children[dragItemIndex.current];
        if (draggedEl) {
          const delta = ev.clientY - dragStartY.current;
          draggedEl.style.transform = `translateY(${delta}px) scale(1.02)`;
          draggedEl.style.zIndex = "50";
          draggedEl.style.opacity = "0.9";
          draggedEl.style.boxShadow = "0 10px 25px -5px rgba(0,0,0,0.15)";
          draggedEl.style.position = "relative";
        }

        // Shift other items using snapshotted heights
        const dragRect = rects[dragItemIndex.current]!;
        for (let i = 0; i < children.length; i++) {
          if (i === dragItemIndex.current) continue;
          const el = children[i]!;
          const gap = i > 0 ? rects[i]!.top - (rects[i - 1]!.top + rects[i - 1]!.height) : 8;
          const itemHeight = dragRect.height + Math.max(gap, 0);
          if (dragItemIndex.current < newIndex && i > dragItemIndex.current && i <= newIndex) {
            el.style.transform = `translateY(-${itemHeight}px)`;
          } else if (
            dragItemIndex.current > newIndex &&
            i < dragItemIndex.current &&
            i >= newIndex
          ) {
            el.style.transform = `translateY(${itemHeight}px)`;
          } else {
            el.style.transform = "";
          }
          el.style.transition = "transform 200ms ease";
        }
      };

      const onUp = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);

        // Reset all transforms
        if (listRef.current) {
          const children = listRef.current.querySelectorAll<HTMLElement>("[data-step-id]");
          children.forEach((el) => {
            el.style.transform = "";
            el.style.zIndex = "";
            el.style.opacity = "";
            el.style.boxShadow = "";
            el.style.position = "";
            el.style.transition = "";
          });
        }

        // Suppress the click event that fires after pointerup
        if (didDrag.current) {
          window.addEventListener(
            "click",
            (ev) => {
              ev.stopPropagation();
              ev.preventDefault();
            },
            { capture: true, once: true },
          );
        }

        setDragState((prev) => {
          const fromIdx = dragItemIndex.current;
          const toIdx = prev.overIndex ?? fromIdx;
          if (fromIdx !== toIdx) {
            const reordered = [...items];
            const [moved] = reordered.splice(fromIdx, 1);
            reordered.splice(toIdx, 0, moved!);
            onReorder(reordered);
          }
          return { draggingId: null, overIndex: null };
        });
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [items, onReorder, getId, enabled],
  );

  const dragHandleProps = useCallback(
    (id: string) => ({
      onPointerDown: (e: React.PointerEvent) => handlePointerDown(id, e),
      style: { touchAction: "none" as const, cursor: enabled ? "grab" : undefined },
    }),
    [handlePointerDown, enabled],
  );

  return { dragHandleProps, dragState, listRef };
}
